import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import contacts from '../../config/contacts';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { logout } from '../../redux/slices/authSlice';
import authService from '../../services/authService';
import { settingsPublicApi } from '../../services/api';
import useTranslations from '../../hooks/useTranslations';

/* ── SVG Icons ── */
const IconBars = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);
const IconClose = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const IconChevronDown = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);
const IconLock = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const IconUser = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="4" />
    <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
  </svg>
);
const IconLogout = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const getInitials = (user) => {
  if (user?.first_name || user?.last_name) {
    return [user.first_name?.[0] || '', user.last_name?.[0] || ''].join('').toUpperCase() || '?';
  }
  if (user?.email) return user.email[0].toUpperCase();
  return '?';
};

const HomeHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addLanguagePrefix } = useLanguage();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showCountriesDropdown, setShowCountriesDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Transparent header on pages with a dark hero (same as home); solid (light) header
  // everywhere else so it stays visible on light pages (profile, dashboard, etc.).
  const cleanPath = location.pathname.replace(/\/$/, '') || '/';
  const DARK_HERO_ROUTES = ['/country', '/visa-pathways', '/how-it-works', '/fees-scope', '/insights', '/about-us'];
  const hasDarkHero = cleanPath === '/' || DARK_HERO_ROUTES.some((r) => cleanPath === r || cleanPath.startsWith(r + '/'));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60 || !hasDarkHero);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasDarkHero]);

  const { user: reduxUser, isAuthenticated: reduxAuth } = useSelector((state) => ({
    user: state.auth?.user ?? null,
    isAuthenticated: !!state.auth?.isAuthenticated || !!state.auth?.user,
  }));
  const authFromService = authService.isAuthenticated();
  const userFromService = authService.getCurrentUser();
  const isAuthenticated = reduxAuth || authFromService;
  const user = reduxUser || userFromService;

  const lang = 'en';

  const { t } = useTranslations('navigation', lang);

  // URLs have no language prefix anymore; strip legacy prefixes if present
  const getLocalizedPath = (p) => (p || '/').replace(/^\/(en|ro|ru|de|fr|it)(\/|$)/i, '$2') || '/';

  const isActive = (path) => {
    const currentPath = location.pathname;
    const localizedPath = getLocalizedPath(path);
    const normalizedCurrent = currentPath.replace(/\/$/, '');
    const normalizedPath = localizedPath.replace(/\/$/, '');
    if (path === '/' && normalizedCurrent === '') return true;
    return normalizedCurrent === normalizedPath;
  };

  const handleStartHere = () => {
    setShowMobileMenu(false);
    const base = contacts.quizUrl || 'http://localhost:3000';
    const params = new URLSearchParams({ quiz: contacts.defaultQuizSlug || 'default-quiz' });
    window.open(`${base.replace(/\/$/, '')}?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = async () => {
    setShowUserDropdown(false);
    try { await dispatch(logout()).unwrap(); } catch (e) { await dispatch(logout()).unwrap(); }
    navigate(getLocalizedPath('/'));
  };

  const displayName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.first_name || user?.name || user?.email || '';

  const countryLinks = [
    { path: '/country/italy', label: 'Italy' },
    { path: '/country/greece', label: 'Greece' },
    { path: '/country/spain', label: 'Spain' },
    { path: '/country/portugal', label: 'Portugal' },
    { path: '/country/france', label: 'France' },
    { path: '/country/malta', label: 'Malta' },
  ];

  const navLinks = [
    { path: '/visa-pathways', key: 'header.visaPathways', label: 'Visa Pathways' },
    { path: '/how-it-works', key: 'header.howItWorks', label: 'How It Works' },
    { path: '/fees-scope', key: 'header.feesScope', label: 'Fees & Scope' },
    { path: '/insights', key: 'header.insights', label: 'Insights' },
    { path: '/about-us', key: 'header.aboutUs', label: 'About Us' },
  ];

  // Color transitions based on scroll
  const textColor = scrolled ? '#0d1e2e' : '#ffffff';
  const textMuted = scrolled ? 'rgba(13,30,46,0.6)' : 'rgba(255,255,255,0.6)';
  const goldColor = '#b8975a';
  const navyColor = '#0d1e2e';

  return (
    <>
      {/* ── NAV — matches homepagedelvanter.html exactly ── */}
      <nav
        className={`dv-home-nav ${scrolled ? 'dv-home-nav-scrolled' : ''}`}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 200,
          padding: '1.3rem 3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background 0.4s, box-shadow 0.4s',
          background: scrolled ? 'rgba(255,255,255,0.98)' : 'transparent',
          boxShadow: scrolled ? '0 1px 0 rgba(13,30,46,0.1)' : 'none',
        }}
      >
        {/* Logo — text-based */}
        <Link to={getLocalizedPath('/')} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', lineHeight: 1, marginRight: '1.5rem' }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: '1.65rem',
            letterSpacing: '0.06em',
            color: scrolled ? navyColor : '#ffffff',
            transition: 'color 0.4s',
          }}>DELVANTE</span>
          <span style={{
            fontSize: '0.58rem',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: scrolled ? 'rgba(13,30,46,0.55)' : 'rgba(255,255,255,0.55)',
            marginTop: '0.12rem',
            transition: 'color 0.4s',
          }}>Immigration Advisory</span>
        </Link>

        {/* Desktop Nav Links */}
        <ul style={{ display: 'flex', listStyle: 'none', gap: '1.8rem', alignItems: 'center' }} className="dv-nav-desktop">
          <li>
            <Link to={getLocalizedPath('/')} style={{
              textDecoration: 'none', fontSize: '0.72rem', fontWeight: 400,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: scrolled ? navyColor : 'rgba(255,255,255,0.82)',
              transition: 'opacity 0.25s', position: 'relative',
            }} className="dv-nav-link-underline">{t('header.home', 'Home')}</Link>
          </li>
          <li className="dv-nav-start-li">
            <button onClick={handleStartHere} style={{
              background: goldColor, color: navyColor,
              padding: '0.5rem 1.2rem', fontWeight: 600, letterSpacing: '0.12em',
              fontSize: '0.72rem', textTransform: 'uppercase',
              border: 'none', cursor: 'pointer', textDecoration: 'none',
              display: 'inline-block', transition: 'background 0.25s, box-shadow 0.25s',
            }} className="dv-nav-start-btn"
              onMouseEnter={e => { e.target.style.background = '#e8dfd0'; e.target.style.boxShadow = '0 4px 18px rgba(184,151,90,0.4)'; }}
              onMouseLeave={e => { e.target.style.background = goldColor; e.target.style.boxShadow = 'none'; }}
            >{t('header.startHere', 'Start Here')}</button>
          </li>
          {navLinks.map((item) => (
            <li key={item.path}>
              <Link to={getLocalizedPath(item.path)} style={{
                textDecoration: 'none', fontSize: '0.72rem', fontWeight: 400,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: scrolled ? navyColor : 'rgba(255,255,255,0.82)',
                transition: 'opacity 0.25s', position: 'relative',
              }} className="dv-nav-link-underline">{t(item.key, item.label)}</Link>
            </li>
          ))}
          {/* Countries Dropdown */}
          <li className="dv-nav-countries-li" style={{ position: 'relative' }}
            onMouseEnter={() => setShowCountriesDropdown(true)}
            onMouseLeave={() => setShowCountriesDropdown(false)}
          >
            <span style={{
              fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.1em',
              textTransform: 'uppercase', cursor: 'pointer',
              color: scrolled ? navyColor : 'rgba(255,255,255,0.82)',
              display: 'flex', alignItems: 'center', gap: '0.25rem',
            }}>{t('header.countries') || 'Countries'} <IconChevronDown /></span>
            <AnimatePresence>
              {showCountriesDropdown && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}
                  style={{
                    position: 'absolute', left: 0, top: '100%', marginTop: '0.5rem',
                    minWidth: '200px', background: '#fff', borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(13,30,46,0.08)', padding: '0.6rem 0', zIndex: 50,
                  }}>
                  {countryLinks.map((c) => (
                    <Link key={c.path} to={getLocalizedPath(c.path)}
                      onClick={() => setShowCountriesDropdown(false)}
                      style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.7rem 1.5rem', fontSize: '0.88rem', fontWeight: 400, color: navyColor, textDecoration: 'none', transition: 'background 0.15s' }}
                      className="dv-dropdown-link"
                    >{c.label}</Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        </ul>

        {/* Right Side */}
        <div className="dv-nav-right" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', marginLeft: '1.5rem' }}>
          {/* Language */}
          {/* Auth: User Menu or Login */}
          {isAuthenticated ? (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowUserDropdown((v) => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: scrolled ? navyColor : '#ffffff',
                  fontSize: '0.8rem', fontWeight: 500,
                }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: scrolled ? navyColor : 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700,
                  color: scrolled ? '#ffffff' : navyColor,
                }}>{getInitials(user)}</div>
                <span className="dv-nav-user-name">{displayName}</span>
                <IconChevronDown />
              </button>
              {showUserDropdown && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setShowUserDropdown(false)} />
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '0.5rem', width: '240px', background: '#fff', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.15)', border: '1px solid rgba(13,30,46,0.08)', padding: '0.5rem 0', zIndex: 50 }}>
                    <div style={{ padding: '0.8rem 1.2rem', borderBottom: '1px solid rgba(13,30,46,0.06)' }}>
                      <p style={{ fontSize: '0.85rem', fontWeight: 600, color: navyColor, margin: 0 }}>{displayName}</p>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(13,30,46,0.5)', margin: '0.2rem 0 0' }}>{user?.email}</p>
                    </div>
                    {[
                      { to: '/dashboard', label: t('header.dashboard') },
                      { to: '/visa/applications', label: t('header.myApplications') },
                      { to: '/profile', label: t('header.profile') },
                      { to: '/profile', label: t('header.settings') },
                    ].map((item, i) => (
                      <Link key={i} to={getLocalizedPath(item.to)} onClick={() => setShowUserDropdown(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 1.2rem', fontSize: '0.82rem', color: navyColor, textDecoration: 'none', transition: 'background 0.15s' }}
                        className="dv-dropdown-link"
                      >{item.label}</Link>
                    ))}
                    <div style={{ borderTop: '1px solid rgba(13,30,46,0.06)', marginTop: '0.25rem', paddingTop: '0.25rem' }}>
                      <button onClick={handleLogout}
                        style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 1.2rem', fontSize: '0.82rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                        className="dv-dropdown-link"
                      ><IconLogout /> {t('header.logout')}</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link
              to={addLanguagePrefix ? addLanguagePrefix('/login') : `/${lang}/login`}
              style={{
                fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.08em',
                textTransform: 'uppercase', padding: '0.5rem 1.2rem', whiteSpace: 'nowrap',
                border: `1px solid ${scrolled ? 'rgba(184,151,90,0.6)' : 'rgba(184,151,90,0.55)'}`,
                color: scrolled ? navyColor : '#ffffff',
                background: scrolled ? 'transparent' : 'rgba(184,151,90,0.25)',
                textShadow: scrolled ? 'none' : '0 1px 4px rgba(0,0,0,0.3)',
                cursor: 'pointer', textDecoration: 'none',
                transition: 'all 0.3s', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                borderRadius: '6px',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
              className="dv-nav-login-btn"
              onMouseEnter={e => {
                e.target.style.background = '#b8975a';
                e.target.style.color = '#0d1e2e';
                e.target.style.borderColor = '#b8975a';
              }}
              onMouseLeave={e => {
                e.target.style.background = scrolled ? 'transparent' : 'rgba(184,151,90,0.25)';
                e.target.style.color = scrolled ? navyColor : '#ffffff';
                e.target.style.borderColor = scrolled ? 'rgba(184,151,90,0.6)' : 'rgba(184,151,90,0.55)';
                e.target.style.textShadow = scrolled ? 'none' : '0 1px 4px rgba(0,0,0,0.3)';
              }}
            ><IconUser /> <span className="dv-nav-login-text">{t('header.membersLogin', 'Members Login')}</span></Link>
          )}

          {/* Mobile Menu Button */}
          <button className="dv-nav-mobile-btn" style={{ display: 'none', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: scrolled ? navyColor : '#ffffff', padding: '0.5rem' }}
            onClick={() => setShowMobileMenu(true)}>
            <IconBars />
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu Overlay ── */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'fixed', inset: 0, background: 'rgba(8,16,26,0.6)', backdropFilter: 'blur(6px)', zIndex: 300 }}
              onClick={() => setShowMobileMenu(false)}
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: '100%', maxWidth: '400px', background: '#fff', boxShadow: '0 0 60px rgba(0,0,0,0.2)', zIndex: 310, overflowY: 'auto', padding: '1.5rem' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', fontWeight: 400, color: navyColor }}>DELVANTE</span>
                <button onClick={() => setShowMobileMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: navyColor, padding: '0.5rem' }}><IconClose /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <Link to={getLocalizedPath('/')} onClick={() => setShowMobileMenu(false)}
                  style={{ padding: '0.85rem 1rem', fontSize: '0.95rem', fontWeight: 500, color: isActive('/') ? goldColor : navyColor, background: isActive('/') ? 'rgba(184,151,90,0.06)' : 'transparent', textDecoration: 'none', borderRadius: '8px' }}>{t('header.home', 'Home')}</Link>
                <button onClick={handleStartHere}
                  style={{ margin: '0.5rem 0', padding: '0.85rem 1rem', fontSize: '0.9rem', fontWeight: 600, color: navyColor, background: goldColor, border: 'none', borderRadius: '8px', cursor: 'pointer', textAlign: 'left' }}>{t('header.startHere', 'Start Here')}</button>
                {navLinks.map((item) => (
                  <Link key={item.path} to={getLocalizedPath(item.path)} onClick={() => setShowMobileMenu(false)}
                    style={{ padding: '0.85rem 1rem', fontSize: '0.95rem', fontWeight: 500, color: isActive(item.path) ? goldColor : navyColor, background: isActive(item.path) ? 'rgba(184,151,90,0.06)' : 'transparent', textDecoration: 'none', borderRadius: '8px' }}>{t(item.key, item.label)}</Link>
                ))}
                <div style={{ borderTop: '1px solid rgba(13,30,46,0.08)', marginTop: '0.75rem', paddingTop: '0.75rem' }}>
                  <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(13,30,46,0.4)', padding: '0 1rem', marginBottom: '0.5rem' }}>Countries</p>
                  {countryLinks.map((c) => (
                    <Link key={c.path} to={getLocalizedPath(c.path)} onClick={() => setShowMobileMenu(false)}
                      style={{ padding: '0.75rem 1rem', fontSize: '0.9rem', fontWeight: 400, color: navyColor, textDecoration: 'none', borderRadius: '8px', display: 'block' }}>{c.label}</Link>
                  ))}
                </div>
                {isAuthenticated ? (
                  <>
                    <div style={{ borderTop: '1px solid rgba(13,30,46,0.08)', marginTop: '0.75rem', paddingTop: '0.75rem' }} />
                    <Link to={getLocalizedPath('/dashboard')} onClick={() => setShowMobileMenu(false)}
                      style={{ padding: '0.85rem 1rem', fontSize: '0.9rem', fontWeight: 500, color: navyColor, textDecoration: 'none', borderRadius: '8px' }}>{t('header.dashboard')}</Link>
                    <button onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                      style={{ padding: '0.85rem 1rem', fontSize: '0.9rem', fontWeight: 500, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: '8px' }}>{t('header.logout')}</button>
                  </>
                ) : (
                  <Link to={addLanguagePrefix ? addLanguagePrefix('/login') : `/${lang}/login`} onClick={() => setShowMobileMenu(false)}
                    style={{ display: 'block', marginTop: '1rem', padding: '0.9rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600, color: navyColor, background: goldColor, textDecoration: 'none', borderRadius: '8px' }}>{t('header.membersLogin', 'Members Login')}</Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── INLINE STYLES ── */}
      <style>{`
        .dv-nav-desktop { display: flex; }
        .dv-nav-desktop li { white-space: nowrap; flex-shrink: 1; }
        .dv-nav-desktop a, .dv-nav-desktop span, .dv-nav-desktop button { white-space: nowrap; }
        .dv-nav-mobile-btn { display: none !important; }
        .dv-home-nav { font-family: 'Jost', sans-serif; }
        @media (max-width: 1150px) {
          .dv-nav-desktop { gap: 1.4rem !important; }
          .dv-nav-desktop a, .dv-nav-desktop span, .dv-nav-desktop button { font-size: 0.66rem !important; }
          .dv-nav-start-li button { padding: 0.4rem 0.9rem !important; font-size: 0.66rem !important; }
          .dv-nav-login-btn { padding: 0.4rem 0.8rem !important; font-size: 0.62rem !important; }
        }
        @media (max-width: 1100px) {
          .dv-nav-desktop { gap: 1.2rem !important; }
          .dv-nav-desktop a, .dv-nav-desktop span, .dv-nav-desktop button { font-size: 0.62rem !important; letter-spacing: 0.06em !important; }
          .dv-nav-start-li button { padding: 0.35rem 0.7rem !important; font-size: 0.62rem !important; }
          .dv-nav-login-btn { padding: 0.35rem 0.7rem !important; font-size: 0.6rem !important; }
          .dv-nav-countries-li { display: none !important; }
        }
        @media (max-width: 1024px) {
          .dv-nav-desktop { display: none !important; }
          .dv-nav-mobile-btn { display: flex !important; }
          .dv-nav-user-name { display: none; }
          .dv-nav-lang-wrap { display: none; }
          .dv-home-nav { padding: 1rem 1.5rem !important; }
          .dv-nav-login-text { display: none !important; }
          .dv-nav-login-btn { padding: 0.5rem !important; }
          .dv-nav-right { gap: 0.6rem !important; margin-left: 0 !important; }
        }
        .dv-nav-link-underline::after {
          content: '';
          position: absolute;
          bottom: -3px; left: 0;
          width: 0%; height: 1px;
          background: #b8975a;
          transition: width 0.3s ease;
        }
        .dv-nav-link-underline:hover::after { width: 100%; }
        .dv-nav-link-underline:hover { opacity: 1 !important; }
        .dv-dropdown-link:hover { background: rgba(13,30,46,0.04); }
      `}</style>
    </>
  );
};

export default HomeHeader;
